import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ArrowLeftIcon, BuildingIcon, WalletIcon, CheckCircle2Icon, PaperclipIcon, XIcon, Loader2Icon, ReceiptIcon, InfoIcon, CalendarIcon, TagIcon, SparklesIcon } from 'lucide-react';
import AttachmentPreviewCard from './AttachmentPreviewCard';
import { toast } from 'sonner';
import SoloFirstExpenseCelebration from './SoloFirstExpenseCelebration';
import ExpenseCategoryFormModal from './ExpenseCategoryFormModal';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import SupplierAutocomplete from './SupplierAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  {
    id: 'company',
    title: 'Şirket Harcaması',
    subtitle: 'Şirket hesabından/kasasından ödendi',
    Icon: BuildingIcon,
    color: 'blue',
  },
  {
    id: 'personal',
    title: 'Şahsi Harcama',
    subtitle: 'Kişisel harcamalar',
    Icon: WalletIcon,
    color: 'emerald',
  },
];

const SoloExpenseEntry = () => {
  const navigate = useNavigate();
  // Single-page form — Şirket/Kişisel chosen via a top-of-form segmented toggle
  // instead of a separate type-picker step.
  const [form, setForm] = useState({
    expense_type: 'company',  // default to Şirket; user can flip to personal
    date: new Date().toISOString().slice(0, 10),
    supplier: '',
    description: '',
    category: '',           // ana kategori adı (veya "Fatura: X")
    subcategory: '',        // alt kategori adı — opsiyonel, sadece parent'ın alt kategorisi varsa
    amount: '',
    currency: 'TRY',
    cost_center_type: 'general_expense',
    cost_center_id: '',
    activity_id: '',
    payment_method: 'cash',  // 'cash' | 'credit_card'
    credit_card_id: '',      // sadece payment_method === 'credit_card' iken doldurulur
    attached_file: null,
  });
  // Kaydedilmeyen değişiklik uyarısı — mevcut form değeri başlangıçtan
  // farklıysa (isDirty), tarayıcı sayfa dışına çıkışta uyarır ve İptal
  // butonu window.confirm ile onaylatır. `submitted` set edildikten
  // sonra uyarı devre dışı kalır (kullanıcı zaten kaydetti).
  const initialFormRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  // Harcama Kategorileri — Ayarlar > Harcama Kategorileri'nden gelir.
  // Her item: `{ id, name, subcategories: [{ id, name }] }`. Ultra Admin'in
  // eski "Genel Gider Türleri" listesinden değil, kullanıcının kendi tenant
  // ayarlarından çekilir.
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [billTypes, setBillTypes] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  // Ayarlar > Uygulama Ayarları > Para Birimleri Yönetimi'nden gelen izinli
  // para birimleri (base + max 4 ek). Boş kalırsa TRY fallback.
  const [allowedCurrencies, setAllowedCurrencies] = useState(['TRY']);
  const fileInputRef = useRef(null);

  // Fetch category options (Masraf Merkezleri → Genel Gider Türleri), project
  // list (used only when expense_type === 'company'), and configured Bill Types
  // ("Faturalar"). Bill Types appear as a grouped section in the Kategori
  // dropdown so the user can pick a configured bill (e.g. "Elektrik — TEDAŞ").
  React.useEffect(() => {
    // İlk render'da form snapshot'ını al — sonradan değişiklik olup
    // olmadığını karşılaştırmak için.
    if (!initialFormRef.current) initialFormRef.current = form;
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${BACKEND_URL}/api/harcama-kategorileri`, { headers: h })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        setCategories(list);
      })
      .catch(() => setCategories([]));
    axios.get(`${BACKEND_URL}/api/cost-centers/projects`, { headers: h })
      .then((r) => setProjects(r.data?.projects || r.data || []))
      .catch(() => setProjects([]));
    axios.get(`${BACKEND_URL}/api/cost-centers/activities`, { headers: h })
      .then((r) => setActivities(r.data?.activities || r.data || []))
      .catch(() => setActivities([]));
    // İzinli para birimleri — kullanıcı Ayarlar > Uygulama Ayarları > Para
    // Birimleri'nden yönetir. Base + allowed birleşimi, sırasız gelir.
    axios.get(`${BACKEND_URL}/api/advance-requests/currencies`, { headers: h })
      .then((r) => {
        const list = r.data?.all_currencies || [];
        setAllowedCurrencies(list.length ? list : ['TRY']);
        // Form default'unu ilk izinli currency'e sabitle (base_currency ilk sırada).
        setForm((prev) => (prev.currency && list.includes(prev.currency))
          ? prev
          : { ...prev, currency: r.data?.base_currency || list[0] || 'TRY' });
      })
      .catch(() => setAllowedCurrencies(['TRY']));
    axios.get(`${BACKEND_URL}/api/bill-types`, { headers: h })
      .then((r) => setBillTypes(r.data?.bill_types || r.data || []))
      .catch(() => setBillTypes([]));
    // Ödeme türü "Kredi Kartı" seçildiğinde ikinci dropdown için kart listesi.
    axios.get(`${BACKEND_URL}/api/credit-cards`, { headers: h })
      .then((r) => setCreditCards(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCreditCards([]));
  }, []);

  // isDirty — mevcut form değeri başlangıçtan farklı mı? Basit JSON
  // karşılaştırması (form küçük ve serialize edilebilir olduğu için OK).
  const isDirty = !submitted
    && initialFormRef.current
    && JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  // Tarayıcı seviyesi navigasyon uyarısı (refresh, sekme kapatma, dış
  // link). Modern tarayıcılar returnValue içeriğini göstermek yerine
  // standart bir onay dialoğu açar. Sadece `isDirty === true` iken.
  React.useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Chrome bunu görmezden geliyor ama standart string döner
      e.returnValue = 'Kaydedilmeyen değişiklikler var. Çıkmak istediğinize emin misiniz?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Uygulama içi navigasyon — İptal butonu ve üst geri butonunda çağrılır.
  // Değişiklik varsa window.confirm ile onaylatır.
  const navigateAway = (route) => {
    if (isDirty) {
      const ok = window.confirm(
        'Kaydedilmeyen değişiklikleriniz var. Bu sayfayı bırakmak istiyor musunuz?'
      );
      if (!ok) return;
    }
    navigate(route);
  };

  // Debounced sessiz otomatik doldurma: kullanıcı işletme adı yazınca
  // (örn. "Shell"), aynı kullanıcının geçmiş kayıtlarında bu işletmeyle
  // eşleşen bir kategori (+ varsa alt kategori) varsa Kategori dropdown'una
  // otomatik olarak yerleştirilir. Kullanıcıya soru soran pill artık YOK —
  // dolduğunda kullanıcı istediği zaman elle değiştirebilir.
  //
  // Kurallar:
  //   • Sadece `source === 'history'` — yani kullanıcının kendi kaydettiği
  //     veri kullanılır (statik pattern kaldırıldı, "Shell → Yakıt" gibi
  //     tahminler otomatik atanmaz).
  //   • form.category veya form.subcategory zaten manuel olarak
  //     doldurulmuşsa üstüne YAZILMAZ (kullanıcının seçimini korur).
  React.useEffect(() => {
    if (!form.supplier || form.supplier.trim().length < 2) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const token = localStorage.getItem('token');
      axios.get(`${BACKEND_URL}/api/solo/category-suggestion`, {
        params: { supplier: form.supplier.trim() },
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
        .then((r) => {
          const d = r.data || {};
          // Yalnızca kullanıcının kendi geçmişinden gelen öneriyi kabul et.
          if (d.source !== 'history') return;
          if (!d.suggested_category) return;
          setForm((prev) => {
            // Kullanıcı zaten bir kategori seçtiyse dokunma.
            if (prev.category) return prev;
            return {
              ...prev,
              category: d.suggested_category,
              // Alt kategori de sadece boşsa ve backend döndürdüyse dolsun.
              subcategory: prev.subcategory || d.suggested_subcategory || '',
            };
          });
        })
        .catch(() => { /* sessizce yut — otomatik doldurma opsiyonel */ });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.supplier]);

  const finishCelebration = () => {
    setShowCelebration(false);
    navigate('/dashboard/expenses');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'ı geçemez');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload-expense-file`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setForm((prev) => ({ ...prev, attached_file: resp.data }));
      toast.success('Belge eklendi');
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || 'Belge yüklenemedi';
      toast.error(typeof msg === 'string' ? msg : 'Belge yüklenemedi');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachedFile = () => {
    setForm((prev) => ({ ...prev, attached_file: null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Tutar 0\'dan büyük olmalı');
      return;
    }
    if (form.payment_method === 'credit_card' && !form.credit_card_id) {
      toast.error('Lütfen bir kredi kartı seçin');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${BACKEND_URL}/api/solo/expenses`, {
        ...form,
        amount: parseFloat(form.amount),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Harcama başarıyla kaydedildi 🎉');
      // Kaydet başarılı → dirty check devre dışı (kullanıcı zaten submit
      // etti, ardından navigate ediyoruz).
      setSubmitted(true);
      if (resp.data?.is_first_expense) {
        // Show celebration + 3-step tutorial. Navigation deferred until modal closes.
        setShowCelebration(true);
      } else {
        navigate('/dashboard/expenses');
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail?.message || err.response?.data?.detail || 'Kaydetme başarısız';
      toast.error(typeof msg === 'string' ? msg : 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Live-özet paneli için okunabilir kategori/proje/harcama tipi metinleri.
  // Formun sağ tarafında kullanıcı yazdıkça anlık güncellenir.
  const selectedTypeMeta = TYPES.find((t) => t.id === form.expense_type);
  // Yeni kategori ekleme modal state — Kategori dropdown'unda "+ Yeni
  // kategori ekle" seçildiğinde `ExpenseCategoryFormModal` açılır (Ayarlar
  // sayfasıyla birebir aynı modal; alt kategori ekleme dahil).
  const [showAddCategory, setShowAddCategory] = React.useState(false);
  // Yeni parent kategori mi, mevcut parent'a alt kategori mi ekliyoruz?
  // `editingParentCategory` null ise yeni parent, doluysa mevcut parent düzenlenir.
  const [editingParentCategory, setEditingParentCategory] = React.useState(null);
  // Bu değer sadece "yakın zamanda önerilen" gibi bir default olarak modal'a
  // aktarılır — modal kendi state'ini yönetir.
  const [newCategoryName, setNewCategoryName] = React.useState('');

  const reloadCategories = async (selectName = null) => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/harcama-kategorileri`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setCategories(list);
      if (selectName) setForm((prev) => ({ ...prev, category: selectName, subcategory: '' }));
    } catch (_e) { /* ignore */ }
  };

  // Proje / Aktivite hızlı ekleme — Kategori ekle akışının aynısı. Dropdown
  // içinden `+ Yeni Proje Ekle` / `+ Yeni Aktivite Ekle` sentinel'i seçilince
  // küçük bir inline dialog açılır, kaydedince liste yenilenip yeni öğe
  // otomatik olarak seçili gelir.
  const [showAddProject, setShowAddProject] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [addingProject, setAddingProject] = React.useState(false);
  const [showAddActivity, setShowAddActivity] = React.useState(false);
  const [newActivityName, setNewActivityName] = React.useState('');
  const [addingActivity, setAddingActivity] = React.useState(false);

  const reloadProjects = async (selectId = null) => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/cost-centers/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = r.data?.projects || r.data || [];
      setProjects(list);
      if (selectId) {
        setForm((prev) => ({
          ...prev,
          cost_center_id: selectId,
          cost_center_type: 'project',
        }));
      }
    } catch (_e) { /* ignore */ }
  };

  const submitNewProject = async () => {
    const name = newProjectName.trim();
    if (!name) {
      toast.error('Proje adı boş olamaz');
      return;
    }
    setAddingProject(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${BACKEND_URL}/api/cost-centers/projects`,
        { project_name: name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newId = r.data?.project?.id;
      toast.success('Proje eklendi ve Masraf Merkezleri\'ne kaydedildi');
      setShowAddProject(false);
      setNewProjectName('');
      await reloadProjects(newId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Proje eklenemedi');
    } finally {
      setAddingProject(false);
    }
  };

  const reloadActivities = async (selectId = null) => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/cost-centers/activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = r.data?.activities || r.data || [];
      setActivities(list);
      if (selectId) {
        setForm((prev) => ({ ...prev, activity_id: selectId }));
      }
    } catch (_e) { /* ignore */ }
  };

  const submitNewActivity = async () => {
    const name = newActivityName.trim();
    if (!name) {
      toast.error('Aktivite adı boş olamaz');
      return;
    }
    setAddingActivity(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${BACKEND_URL}/api/cost-centers/activities`,
        { activity_name: name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newId = r.data?.activity?.id;
      toast.success('Aktivite eklendi ve Masraf Merkezleri\'ne kaydedildi');
      setShowAddActivity(false);
      setNewActivityName('');
      await reloadActivities(newId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktivite eklenemedi');
    } finally {
      setAddingActivity(false);
    }
  };

  const selectedProjectName = form.cost_center_id
    ? (projects.find((p) => p.id === form.cost_center_id)?.project_name
        || projects.find((p) => p.id === form.cost_center_id)?.name
        || '—')
    : null;
  const formattedAmount = form.amount
    ? Number(form.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0,00';
  const currencySymbol = { TRY: '₺', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' }[form.currency] || form.currency;

  return (
    <div className="min-h-full bg-gray-50" data-testid="solo-expense-form">
      {/* Tam-sayfa üst başlık: geri butonu + başlık + kısa açıklama */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigateAway('/dashboard/expenses')}
            className="w-10 h-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center flex-shrink-0"
            data-testid="solo-expense-back"
            aria-label="Geri"
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Yeni Harcama</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 hidden sm:block">
              Tek formda tüm bilgileri girin — sağ tarafta anlık özet.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 text-xs font-medium">
            <ReceiptIcon className="w-4 h-4" />
            Solo Harcama Girişi
          </div>
          {isDirty && (
            <div
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-800 text-xs font-medium border border-amber-200 animate-pulse"
              data-testid="solo-expense-dirty-badge"
              title="Kaydedilmemiş değişiklikleriniz var"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Kaydedilmemiş değişiklikler
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 items-start">
        {/* SOL: Form kartı — 2 sütun kapladır (lg:col-span-2) */}
        <Card className="p-4 sm:p-6 lg:col-span-2">
          <form onSubmit={submit} className="space-y-4">
          {/* Type toggle — Şirket / Kişisel */}
          <div data-testid="solo-form-type-toggle">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tip *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TYPES.map(({ id, title, subtitle, Icon: TIcon, color }) => {
                const active = form.expense_type === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, expense_type: id, cost_center_type: id === 'personal' ? 'general_expense' : p.cost_center_type, cost_center_id: id === 'personal' ? '' : p.cost_center_id }))}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition ${
                      active
                        ? (id === 'personal' ? 'border-emerald-500 bg-emerald-50' : 'border-blue-500 bg-blue-50')
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`solo-type-${id}`}
                  >
                    <TIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${active ? (id === 'personal' ? 'text-emerald-600' : 'text-blue-600') : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900">{title}</div>
                      <div className="text-xs text-gray-500 break-words">{subtitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarih *</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                className="w-full block"
                data-testid="solo-form-date"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Firma / Yer</label>
              <SupplierAutocomplete
                value={form.supplier}
                onChange={(v) => { setForm({ ...form, supplier: v }); }}
                placeholder="Örn: Anadolu Hastanesi"
                testId="solo-form-supplier"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Müşteri toplantısı, ofis malzemesi, vb."
              data-testid="solo-form-description"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tutar *</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
                data-testid="solo-form-amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                data-testid="solo-form-currency"
              >
                {allowedCurrencies.map((code) => {
                  // Basit sembol lookup — bilinmeyen kodlarda kodun kendisini göster.
                  const symbol = { TRY: '₺', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', JPY: '¥', CHF: '₣', CNY: '¥', RUB: '₽' }[code] || '';
                  return (
                    <option key={code} value={code}>{code} {symbol}</option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Ayarlar &gt; Uygulama Ayarları &gt; Para Birimleri&apos;nden yönetilir.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <Select
                value={form.category || ''}
                onValueChange={(v) => {
                  if (v === '__add_new__') {
                    setShowAddCategory(true);
                    return;
                  }
                  setForm({ ...form, category: v, subcategory: '' });
                }}
              >
                <SelectTrigger
                  className="w-full h-10 bg-white border rounded-lg focus:ring-2 focus:ring-purple-500"
                  data-testid="solo-form-category"
                >
                  <SelectValue placeholder="— Seçiniz —" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem
                    value="__add_new__"
                    className="font-semibold text-purple-600"
                    data-testid="solo-form-category-add-new"
                  >
                    + Yeni kategori ekle…
                  </SelectItem>
                  {/* Fallback — form.category listede yoksa görünür kalsın. */}
                  {form.category
                    && form.category !== '__add_new__'
                    && !categories.some((c) => c.name === form.category)
                    && !billTypes.some((b) => {
                         const label = b.custom_label
                           ? `${b.parent_label_tr} — ${b.custom_label}`
                           : b.parent_label_tr;
                         return `Fatura: ${label}` === form.category;
                       }) && (
                    <SelectItem value={form.category} data-testid="solo-form-category-fallback">
                      {form.category}
                    </SelectItem>
                  )}
                  {(() => {
                    const userCats = categories.filter((c) => !c.global_id);
                    const systemCats = categories.filter((c) => !!c.global_id);
                    return (
                      <>
                        {userCats.length > 0 && (
                          <SelectGroup>
                            <SelectLabel className="text-[11px] uppercase tracking-wide text-gray-500">
                              Kullanıcı Kategorileri
                            </SelectLabel>
                            {userCats.map((c) => (
                              <SelectItem key={c.id || c.name} value={c.name}>
                                <span className="flex items-center justify-between gap-2 w-full">
                                  <span>{c.name}</span>
                                  <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[10px] font-medium">
                                    Kullanıcı
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        {systemCats.length > 0 && (
                          <SelectGroup>
                            <SelectLabel className="text-[11px] uppercase tracking-wide text-gray-500">
                              Sistem Kategorileri
                            </SelectLabel>
                            {systemCats.map((c) => (
                              <SelectItem key={c.id || c.name} value={c.name}>
                                <span className="flex items-center justify-between gap-2 w-full">
                                  <span>{c.name}</span>
                                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[10px] font-medium">
                                    Sistem
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </>
                    );
                  })()}
                  {billTypes.length > 0 && (
                    <SelectGroup data-testid="solo-form-category-bills-group">
                      <SelectLabel className="text-[11px] uppercase tracking-wide text-gray-500">
                        Faturalar
                      </SelectLabel>
                      {billTypes.map((b) => {
                        const label = b.custom_label
                          ? `${b.parent_label_tr} — ${b.custom_label}`
                          : b.parent_label_tr;
                        return (
                          <SelectItem key={b.id} value={`Fatura: ${label}`}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {/* Alt kategori dropdown — parent'ın subcategories varsa listeler,
                  yoksa yalnızca "+ Yeni alt kategori ekle…" sentinel'i ile
                  hızlı ekleme yolunu sunar. Faturalarda alt kategori yok
                  (parent `categories` listesinde bulunmadığından render'a girmez). */}
              {(() => {
                const parent = categories.find((c) => c.name === form.category);
                if (!parent) return null;
                const subs = parent.subcategories || [];
                return (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Alt Kategori (Opsiyonel)</label>
                    <select
                      value={form.subcategory}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__add_new_sub__') {
                          // Sentinel — parent'ı düzenleme modunda modal'a aktar.
                          // Kullanıcı yeni alt kategori(ler) ekler, kaydedince
                          // reloadCategories tetiklenir ve dropdown güncellenir.
                          setEditingParentCategory(parent);
                          setShowAddCategory(true);
                          return;
                        }
                        setForm({ ...form, subcategory: v });
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                      data-testid="solo-form-subcategory"
                    >
                      <option value="">— Alt kategori seçiniz —</option>
                      <option value="__add_new_sub__" className="font-semibold text-purple-600">+ Yeni alt kategori ekle…</option>
                      {subs.map((s) => (
                        <option key={s.id || s.name} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
            {/* Proje dropdown — only relevant for company expenses */}
            {form.expense_type === 'company' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proje (Opsiyonel)</label>
              <select
                value={form.cost_center_id}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__add_new_project__') {
                    // Sentinel — mevcut seçim korunsun, modal'ı aç.
                    setNewProjectName('');
                    setShowAddProject(true);
                    return;
                  }
                  setForm({
                    ...form,
                    cost_center_id: v,
                    cost_center_type: v ? 'project' : 'general_expense',
                  });
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                data-testid="solo-form-project"
              >
                <option value="">— Proje yok (Genel Gider) —</option>
                <option value="__add_new_project__" className="font-semibold text-purple-600">+ Yeni Proje Ekle…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.project_name || p.name}</option>
                ))}
              </select>
            </div>
            )}
            {/* Aktivite dropdown — sadece şahsi harcamalarda. Yaz tatili,
                doğum günü gibi kişisel aktivitelere gider bağlamak için. */}
            {form.expense_type === 'personal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aktivite (Opsiyonel)</label>
              <select
                value={form.activity_id}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__add_new_activity__') {
                    setNewActivityName('');
                    setShowAddActivity(true);
                    return;
                  }
                  setForm({ ...form, activity_id: v });
                }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                data-testid="solo-form-activity"
              >
                <option value="">— Aktivite yok —</option>
                <option value="__add_new_activity__" className="font-semibold text-purple-600">+ Yeni Aktivite Ekle…</option>
                {activities.map((a) => (
                  <option key={a.id} value={a.id}>{a.activity_name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Örn: Yaz Tatili, Doğum Günü — Masraf Merkezleri &gt; Aktiviteler&apos;den de ekleyebilirsiniz.
              </p>
            </div>
            )}
          </div>
          {/* Ödeme Türü — Nakit veya Kredi Kartı. Kredi kartı seçilirse
              hemen altında kart seçme dropdown'u çıkar; Nakit'te bu ikinci
              alan gizli. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4" data-testid="solo-form-payment-block">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Türü *</label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({
                  ...form,
                  payment_method: e.target.value,
                  // Nakit seçilirse kart id'si sıfırlanır.
                  credit_card_id: e.target.value === 'cash' ? '' : form.credit_card_id,
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                data-testid="solo-form-payment-method"
              >
                <option value="cash">💵 Nakit</option>
                <option value="credit_card">💳 Kredi Kartı</option>
              </select>
            </div>
            {form.payment_method === 'credit_card' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kart *</label>
                <select
                  value={form.credit_card_id}
                  onChange={(e) => setForm({ ...form, credit_card_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                  data-testid="solo-form-credit-card"
                >
                  <option value="">— Kart seçiniz —</option>
                  {creditCards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.card_name} •{c.last4}
                    </option>
                  ))}
                </select>
                {creditCards.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Henüz kart yok — Ayarlar &gt; Kredi Kartları&apos;ndan ekleyin.
                  </p>
                )}
              </div>
            )}
          </div>
          {/* File Upload (Receipt/Document) */}
          <div data-testid="solo-form-file-upload">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Belge / Fiş (Opsiyonel)
            </label>
            {form.attached_file ? (
              <AttachmentPreviewCard
                file={form.attached_file}
                onRemove={removeAttachedFile}
                testIdPrefix="solo-form-file"
              />
            ) : (
              <label
                htmlFor="solo-file-input"
                className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition ${
                  uploading
                    ? 'border-purple-300 bg-purple-50 cursor-wait'
                    : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2Icon className="w-4 h-4 animate-spin text-purple-600" />
                    <span className="text-sm text-purple-600">Yükleniyor...</span>
                  </>
                ) : (
                  <>
                    <PaperclipIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Fiş veya belge ekleyin (PDF, JPG, PNG · maks 10MB)</span>
                  </>
                )}
                <input
                  id="solo-file-input"
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf,.heic,.heif"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  data-testid="solo-form-file-input"
                />
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => navigateAway('/dashboard/expenses')}>
              İptal
            </Button>
            <Button type="submit" disabled={saving || uploading} data-testid="solo-form-submit">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </form>
      </Card>

      {/* SAĞ: Anlık özet paneli + ipuçları (lg ve üzeri ekranlarda görünür,
          küçük ekranlarda formun altına düşer) */}
      <div className="space-y-4 lg:col-span-1">
        <Card className="p-5" data-testid="solo-expense-summary">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <CheckCircle2Icon className="w-4 h-4 text-teal-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Anlık Özet</h3>
          </div>

          {/* Büyük tutar göstergesi */}
          <div className="text-center mb-5">
            <div className="text-3xl font-bold text-gray-900">
              {formattedAmount}
              <span className="text-lg text-gray-500 ml-1">{currencySymbol}</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Toplam tutar</div>
          </div>

          {/* Detay listesi */}
          <div className="space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                {selectedTypeMeta?.Icon && <selectedTypeMeta.Icon className="w-3.5 h-3.5" />}
                Tip
              </span>
              <span className="font-medium text-gray-900 text-right truncate ml-2 max-w-[60%]">
                {selectedTypeMeta?.title || '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                <CalendarIcon className="w-3.5 h-3.5" /> Tarih
              </span>
              <span className="font-medium text-gray-900">
                {form.date
                  ? new Date(form.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
                  : '—'}
              </span>
            </div>
            <div className="flex items-start justify-between">
              <span className="flex items-center gap-1.5 text-gray-500 flex-shrink-0">
                <BuildingIcon className="w-3.5 h-3.5" /> Firma
              </span>
              <span className="font-medium text-gray-900 text-right truncate ml-2 max-w-[60%]">
                {form.supplier || '—'}
              </span>
            </div>
            <div className="flex items-start justify-between">
              <span className="flex items-center gap-1.5 text-gray-500 flex-shrink-0">
                <TagIcon className="w-3.5 h-3.5" /> Kategori
              </span>
              <span className="font-medium text-gray-900 text-right truncate ml-2 max-w-[60%]">
                {form.category || '—'}
              </span>
            </div>
            {form.expense_type === 'company' && selectedProjectName && (
              <div className="flex items-start justify-between">
                <span className="text-gray-500 flex-shrink-0">Proje</span>
                <span className="font-medium text-gray-900 text-right truncate ml-2 max-w-[60%]">
                  {selectedProjectName}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                <PaperclipIcon className="w-3.5 h-3.5" /> Belge
              </span>
              <span className={`font-medium ${form.attached_file ? 'text-teal-600' : 'text-gray-400'}`}>
                {form.attached_file ? 'Eklendi' : 'Yok'}
              </span>
            </div>
            <div className="flex items-start justify-between" data-testid="solo-summary-payment">
              <span className="flex items-center gap-1.5 text-gray-500 flex-shrink-0">
                <span className="text-sm leading-none">
                  {form.payment_method === 'credit_card' ? '💳' : '💵'}
                </span>
                Ödeme
              </span>
              <span className="font-medium text-gray-900 text-right truncate ml-2 max-w-[60%]">
                {form.payment_method === 'credit_card'
                  ? (() => {
                      const c = creditCards.find((x) => x.id === form.credit_card_id);
                      return c ? `${c.card_name} •${c.last4}` : 'Kart seçilmedi';
                    })()
                  : 'Nakit'}
              </span>
            </div>
          </div>
        </Card>

        {/* İpuçları kartı */}
        <Card className="p-5 bg-amber-50 border-amber-200" data-testid="solo-expense-tips">
          <div className="flex items-center gap-2 mb-3">
            <InfoIcon className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-semibold text-amber-900">Hızlı İpuçları</h3>
          </div>
          <ul className="space-y-2 text-xs text-amber-800 leading-relaxed">
            <li className="flex gap-2">
              <span className="text-amber-500">•</span>
              <span>Fişin fotoğrafını yüklerseniz aylık raporda otomatik olarak muhasebecinize gider.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-500">•</span>
              <span>Aynı işletmeye 2. kez harcama yazarken sistem uyarır — mükerrer kayıt engellenir.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-amber-500">•</span>
              <span>USD/EUR/AED seçerseniz raporda hem orijinal hem TRY karşılığı görünür.</span>
            </li>
          </ul>
        </Card>
      </div>
      </div>
      <SoloFirstExpenseCelebration
        open={showCelebration}
        onFinish={finishCelebration}
      />

      {/* Hızlı Kategori/Proje/Aktivite Ekleme Modal'ları — dropdown'ın içinden
          "+ Yeni ... Ekle…" seçildiğinde sayfayı terk etmeden kaydeder ve
          yeni öğeyi otomatik seçer. */}
      <ExpenseCategoryFormModal
        open={showAddCategory}
        onOpenChange={(o) => {
          setShowAddCategory(o);
          if (!o) {
            setNewCategoryName('');
            setEditingParentCategory(null);
          }
        }}
        editingCategory={editingParentCategory}
        initialName={editingParentCategory ? '' : newCategoryName}
        onSaved={async (saved) => {
          // Ayarlar > Harcama Kategorileri ile aynı endpoint'e yazar. Ardından
          // dropdown listesini yenile ve yeni parent adını otomatik seç.
          // Alt kategori ekleme akışında parent zaten seçili — sadece listeyi
          // yenile, seçim değişmesin.
          const wasEditingParent = !!editingParentCategory;
          setNewCategoryName('');
          setEditingParentCategory(null);
          if (wasEditingParent) {
            // Sadece listeyi yenile; parent seçimi ve mevcut alt kategori korunsun.
            await reloadCategories();
          } else {
            // Yeni parent kategori eklendi → otomatik seç.
            await reloadCategories(saved?.name);
          }
        }}
      />
      {showAddProject && (
        <QuickAddModal
          title="Yeni Proje Ekle"
          label="Proje Adı"
          placeholder="Örn: Web Sitesi Yenileme"
          value={newProjectName}
          onChange={setNewProjectName}
          onCancel={() => { setShowAddProject(false); setNewProjectName(''); }}
          onSubmit={submitNewProject}
          saving={addingProject}
          testIdPrefix="quick-add-project"
        />
      )}
      {showAddActivity && (
        <QuickAddModal
          title="Yeni Aktivite Ekle"
          label="Aktivite Adı"
          placeholder="Örn: Yaz Tatili"
          value={newActivityName}
          onChange={setNewActivityName}
          onCancel={() => { setShowAddActivity(false); setNewActivityName(''); }}
          onSubmit={submitNewActivity}
          saving={addingActivity}
          testIdPrefix="quick-add-activity"
        />
      )}
    </div>
  );
};

// Reusable küçük modal — Kategori/Proje/Aktivite hızlı ekleme için ortak UI.
const QuickAddModal = ({ title, label, placeholder, value, onChange, onCancel, onSubmit, saving, testIdPrefix }) => {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    // Modal açılır açılmaz input'a fokus.
    if (inputRef.current) inputRef.current.focus();
  }, []);
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid={`${testIdPrefix}-modal`}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim() && !saving) {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          data-testid={`${testIdPrefix}-input`}
        />
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            data-testid={`${testIdPrefix}-cancel`}
          >
            İptal
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving || !value.trim()}
            className="px-4 py-2 rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            data-testid={`${testIdPrefix}-submit`}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoloExpenseEntry;
